import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './paymentProcessing.module.css';
import paymentProcessingIcon from '../../../assets/images/verticaltv/Frame 1597880675.svg';
import { NAVIGATION_ROUTES } from '../../../constant';
import { useGetSubscriptionStatus } from '../../../api/getSubscriptionStatus';
import { CLEVERTAP_EVENTS } from '../../../analytics/trackerEvents/cleverTap/events';
import { CT_PARAMS } from '../../../analytics/trackerEvents/cleverTap/parameters';
import { trackCleverTapEvent } from '../../../utils/cleverTapHelper';
import { FbPixelEvent_StartTrial, FbPixelEvent_Subscribe } from '../../../utils/FbPixel/fbPixel';
import { fireStartTrialEvent, fireSubscribeEvent } from '../../../utils/SmartScriptAppFlyer/appsFlyerEvents';
import { useTokenStore } from '../../../storage/tokenState';
import { pushKafkaEvent } from '../../../api/pushKafkaEvent';
import { GA_EVENTS, pushGAEvent } from '../../../utils/gaHelper';

const PaymentProcessing: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orderId, setOrderId] = useState<string | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState<boolean>(false);
  const { checkSubscriptionStatus, isLoading } = useGetSubscriptionStatus();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollAttemptsRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Check if this is UPI collect flow (5 minutes) or normal UPI flow (10 seconds)
  const isUpiCollectFlow = sessionStorage.getItem('isUpiCollectFlow') === 'true';
  const maxPollAttempts = isUpiCollectFlow ? 60 : 10; // 5 minutes for UPI collect (60 attempts * 5s = 5 minutes), 10 seconds for normal UPI
  const pollInterval = isUpiCollectFlow ? 5000 : 1000; // Poll every 5 seconds for UPI collect, 1 second for normal UPI
  const maxPollingTime = maxPollAttempts * pollInterval; // Total time in milliseconds
  const failedResponseThreshold = 5; // Threshold for failed responses before navigating to failed screen
  const { token } = useTokenStore();

  // Function to handle timeout navigation (backup mechanism)
  const handleTimeoutNavigation = useCallback(() => {
    console.log('[TIMEOUT] Max polling time reached. Navigating to subscription payment screen...');
    
    // Clear stored data
    sessionStorage.removeItem('pendingOrderId');
    sessionStorage.removeItem('paymentInitiated');
    sessionStorage.removeItem('paymentTimestamp');
    sessionStorage.removeItem('pendingPaymentAmount');
 
    sessionStorage.removeItem('mandateUri');
    sessionStorage.removeItem('mandateUriOpened');
    sessionStorage.removeItem('shouldNavigateToProcessing');
    sessionStorage.removeItem('isUpiCollectFlow');
    
    // Stop polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Navigate to subscription payment screen
    navigate('/trial/subscription-payment');
  }, [navigate]);

  // Function to check subscription status and navigate accordingly
  const checkPaymentStatus = useCallback(async () => {
    // Better check - don't block if isCheckingStatus is true, just skip this attempt
    if (pollAttemptsRef.current >= maxPollAttempts) {
      console.log('[POLLING] Max polling attempts reached, navigating to subscription payment screen...');
      
      // Clear stored data
      sessionStorage.removeItem('pendingOrderId');
      sessionStorage.removeItem('paymentInitiated');
      sessionStorage.removeItem('paymentTimestamp');
      sessionStorage.removeItem('pendingPaymentAmount');
      
      sessionStorage.removeItem('mandateUri');
      sessionStorage.removeItem('mandateUriOpened');
      sessionStorage.removeItem('shouldNavigateToProcessing');
      sessionStorage.removeItem('isUpiCollectFlow');
      
      // Stop polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      // Navigate to subscription payment screen
      navigate('/trial/subscription-payment');
      return;
    }

    // Skip if already checking (but don't block forever)
    if (isCheckingStatus) {
      console.log('[POLLING] Already checking status, skipping this attempt...');
      return;
    }

    setIsCheckingStatus(true);
    pollAttemptsRef.current += 1;

    try {
      console.log(`[POLLING] Attempt ${pollAttemptsRef.current}/${maxPollAttempts} - Checking subscription status...`);
      const response = await checkSubscriptionStatus();

      console.log('[POLLING] Full API Response:', JSON.stringify(response, null, 2));

      if (response?.response?.success) {
        const activeSubscriptionsData = response.response.data?.activeSubscriptionsData;
        
        console.log('[POLLING] activeSubscriptionsData:', activeSubscriptionsData);
        
        // Check if activeSubscriptionsData contains ZUPEE_TV object with isActive: true
        if (activeSubscriptionsData?.ZUPEE_TV?.isActive === true) {
          // Payment successful - navigate to subscribed screen
          console.log('[POLLING] ? Subscription is active! Navigating to subscribed screen...');
          
          // Track CleverTap event for successful payment
          const userId = token?.zupeeUserId?.toString() || '';
          const currentOrderId = sessionStorage.getItem('pendingOrderId') || '';
          const paymentAmount = parseInt(sessionStorage.getItem('pendingPaymentAmount') || '0', 10);
          const displayAmount = parseInt(sessionStorage.getItem('pendingPaymentDisplayAmount') || '0', 10);
          const userMobile = token?.signupPhoneNumber || token?.un || '';
          // Use displayAmount to determine trial (displayAmount is 2 for trial, but paymentAmount might be 549)
          const isTrialPayment = displayAmount === 2;
          const paymentType = isTrialPayment ? CT_PARAMS.TRIAL : CT_PARAMS.SUBSCRIPTION;
          
          trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_PAYMENT_STATUS_SCREEN_VIEW, {
            [CT_PARAMS.USER_ID]: userId,
            [CT_PARAMS.ORDER_ID]: currentOrderId,
            [CT_PARAMS.STATUS_OF_PAYMENT]: 'success',
            [CT_PARAMS.PACK_AMOUNT]: paymentAmount,
            [CT_PARAMS.TYPE]: paymentType,
            [CT_PARAMS.MOBILE_NUMBER]: userMobile
          });
          
          if (paymentAmount === 2) {
            // 2 rupees trial - StartTrial event
            // Use displayAmount (2) for the event value, not paymentAmount (which might be 549)
            FbPixelEvent_StartTrial(paymentAmount);
            // Track AppsFlyer Trial event
            fireStartTrialEvent({ trialDays: 7, plan: 'free_trial' });
            
            // Push trial_started event to Kafka (non-blocking) with paymentAmount
            pushKafkaEvent('trial_started', userId, userMobile, token?.zupeeUserId, paymentAmount).catch(err => {
              console.log('[PaymentProcessing] Kafka trial_started event failed (non-critical):', err);
            });

            // Track GA event for trial payment success
            pushGAEvent(GA_EVENTS.TRIAL_PAYMENT_SUCCESS, {
              order_id: currentOrderId,
              amount: paymentAmount, 
              user_id: userId
            });
          } else {
            // Paid subscription - Subscribe event
            // FbPixelEvent_Subscribe(paymentAmount);
            // Track AppsFlyer Subscribe event
            fireSubscribeEvent({ plan: 'paid', price: paymentAmount, currency: 'INR' });
            
            // Push subscribe_started event to Kafka (non-blocking) with paymentAmount
            pushKafkaEvent('subscribe_started', userId, userMobile, token?.zupeeUserId, paymentAmount).catch(err => {
              console.log('[PaymentProcessing] Kafka subscribe_started event failed (non-critical):', err);
            });

            // Track GA event for subscription payment success
            pushGAEvent(GA_EVENTS.SUBSCRIPTION_PAYMENT_SUCCESS, {
              order_id: currentOrderId,
              amount: paymentAmount,
              user_id: userId,
              user_mobile: userMobile,
            });
          }
          
          // Clear all stored payment data
          sessionStorage.removeItem('pendingOrderId');
          sessionStorage.removeItem('paymentInitiated');
          sessionStorage.removeItem('paymentTimestamp');
          sessionStorage.removeItem('pendingPaymentAmount');
         
          sessionStorage.removeItem('mandateUri');
          sessionStorage.removeItem('mandateUriOpened');
          sessionStorage.removeItem('shouldNavigateToProcessing');
          sessionStorage.removeItem('isUpiCollectFlow');
          
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          // Clear timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          
          // Navigate to subscribed screen
          navigate('/trial/subscribed', {
            state: { fromPaymentProcessing: true }
          });
          return;
        } else if (activeSubscriptionsData && Object.keys(activeSubscriptionsData).length === 0) {
          // Empty object - check if we've reached max attempts
          if (pollAttemptsRef.current >= maxPollAttempts) {
            console.log('[POLLING] ?? Max polling attempts reached with empty subscription data. Navigating to subscription payment screen...');
            
            // Clear stored data
            sessionStorage.removeItem('pendingOrderId');
            sessionStorage.removeItem('paymentInitiated');
            sessionStorage.removeItem('paymentTimestamp');
            sessionStorage.removeItem('pendingPaymentAmount');
           
            sessionStorage.removeItem('mandateUri');
            sessionStorage.removeItem('mandateUriOpened');
            sessionStorage.removeItem('shouldNavigateToProcessing');
            
            // Stop polling
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            
            // Clear timeout
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            
            // Navigate to subscription payment screen
            navigate('/trial/subscription-payment');
            return;
          }
          
          // Continue polling if we haven't reached max attempts
          console.log('[POLLING] ? Subscription data is empty, will retry...', {
            attempts: pollAttemptsRef.current,
            maxAttempts: maxPollAttempts
          });
          
          setIsCheckingStatus(false);
          return;
        } else {
          // Subscription not active yet - check if we've reached max attempts
          if (pollAttemptsRef.current >= maxPollAttempts) {
            console.log('[POLLING] ?? Max polling attempts reached without active subscription. Navigating to subscription payment screen...');
            
            // Clear stored data
            sessionStorage.removeItem('pendingOrderId');
            sessionStorage.removeItem('paymentInitiated');
            sessionStorage.removeItem('paymentTimestamp');
            sessionStorage.removeItem('pendingPaymentAmount');
            
            sessionStorage.removeItem('mandateUri');
            sessionStorage.removeItem('mandateUriOpened');
            sessionStorage.removeItem('shouldNavigateToProcessing');
            
            // Stop polling
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            
            // Clear timeout
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            
            // Navigate to subscription payment screen
            navigate('/trial/subscription-payment');
            return;
          }
          
          // Continue polling if we haven't reached max attempts
          console.log('[POLLING] ? Subscription not active yet, will retry...', {
            hasZUPEE_TV: !!activeSubscriptionsData?.ZUPEE_TV,
            isActive: activeSubscriptionsData?.ZUPEE_TV?.isActive,
            attempts: pollAttemptsRef.current,
            maxAttempts: maxPollAttempts
          });
          
          setIsCheckingStatus(false);
          return;
        }
      } else {
        // API returned failed response
        const responseAny = response as any;
        console.warn('[POLLING] ? API returned failed response:', {
          success: response?.response?.success,
          message: responseAny?.response?.message,
          error: responseAny?.response?.error,
          attempts: pollAttemptsRef.current,
          threshold: failedResponseThreshold
        });
        
        // Use correct threshold
        if (pollAttemptsRef.current >= failedResponseThreshold) {
          console.error('[POLLING] Multiple failed responses received. Navigating to payment failed screen...');
          
          // Clear stored data
          sessionStorage.removeItem('pendingOrderId');
          sessionStorage.removeItem('paymentInitiated');
          sessionStorage.removeItem('paymentTimestamp');
          sessionStorage.removeItem('pendingPaymentAmount');
         
          sessionStorage.removeItem('mandateUri');
          sessionStorage.removeItem('mandateUriOpened');
          sessionStorage.removeItem('shouldNavigateToProcessing');
          
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          // Clear timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          
          // Navigate to payment failed screen
          navigate('/trial/payment-failed');
          return;
        } else {
          // Continue polling on failed response (might be temporary)
          console.warn('[POLLING] Will retry after failed response...');
          setIsCheckingStatus(false);
          return;
        }
      }
    } catch (error: any) {
      console.error('[POLLING] ? Error checking subscription status:', {
        error,
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        attempts: pollAttemptsRef.current,
        maxAttempts: maxPollAttempts
      });
      
      // Always reset isCheckingStatus in catch block
      setIsCheckingStatus(false);
      
      // If we've exhausted all attempts, navigate to subscription payment screen
      if (pollAttemptsRef.current >= maxPollAttempts) {
        console.error('[POLLING] Max polling attempts reached. Navigating to subscription payment screen...');
        
        // Clear stored data
        sessionStorage.removeItem('pendingOrderId');
        sessionStorage.removeItem('paymentInitiated');
        sessionStorage.removeItem('paymentTimestamp');
        sessionStorage.removeItem('pendingPaymentAmount');
       
        sessionStorage.removeItem('mandateUri');
        sessionStorage.removeItem('mandateUriOpened');
        sessionStorage.removeItem('shouldNavigateToProcessing');
        
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        // Clear timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        
        // Navigate to subscription payment screen
        navigate('/trial/subscription-payment');
      }
      // Don't return here - let polling continue if attempts < max
    }
  }, [isCheckingStatus, checkSubscriptionStatus, navigate, maxPollAttempts, failedResponseThreshold]);

  // Start polling when component mounts with orderId
  useEffect(() => {
    // Get orderId from location state or sessionStorage
    const stateOrderId = location.state?.orderId;
    const storedOrderId = sessionStorage.getItem('pendingOrderId');
    
    const finalOrderId = stateOrderId || storedOrderId;
    
    // Set orderId if available (but don't require it for polling)
    if (finalOrderId) {
      setOrderId(finalOrderId);
    }
    
    // Check if UPI app was opened or payment is in progress (this indicates user should see polling)
    const mandateUriOpened = sessionStorage.getItem('mandateUriOpened');
    
    // Start polling if:
    // 1. mandateUriOpened is set (user returned from payment or payment is in progress)
    // 2. OR orderId exists (payment was initiated)
    // Polling doesn't require orderId - it just checks subscription status
    if ((mandateUriOpened === 'true' || finalOrderId) && !pollingIntervalRef.current) {
      console.log('Starting subscription status polling...', {
        mandateUriOpened,
        hasOrderId: !!finalOrderId,
        orderId: finalOrderId
      });
      
      // Reset polling attempts
      pollAttemptsRef.current = 0;
      
      // Start polling after a short delay to ensure component is ready
      const startPolling = () => {
        // Check if polling already started (prevent duplicate starts)
        if (pollingIntervalRef.current) {
          console.log('Polling already started, skipping...');
          return;
        }
        
        // Start immediate check
        checkPaymentStatus();
        
        // Set up polling interval
        pollingIntervalRef.current = setInterval(() => {
          // Always check if we've reached max attempts first
          if (pollAttemptsRef.current >= maxPollAttempts) {
            // Stop polling if max attempts reached
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            
            // Final check to ensure we navigate if max attempts reached
            // This handles the case where the last attempt didn't trigger navigation
            console.log('[POLLING] Max attempts reached in interval. Performing final check and navigation...');
            checkPaymentStatus();
            return;
          }
          
          // Continue polling if we haven't reached max attempts
          checkPaymentStatus();
        }, pollInterval);
        
        // Set up timeout as backup mechanism to ensure navigation after max time
        timeoutRef.current = setTimeout(() => {
          console.log('[TIMEOUT] Max polling time elapsed. Triggering timeout navigation...');
          handleTimeoutNavigation();
        }, maxPollingTime);
      };
      
      // Start polling after a short delay
      setTimeout(startPolling, 1000);
    } else if (!mandateUriOpened && !finalOrderId) {
      // No orderId and no mandateUriOpened flag - likely navigated here by mistake
      console.log('No payment in progress, navigating back to payment page...');
      navigate('/trial/subscription-payment');
    }
  }, [location, navigate, checkPaymentStatus, handleTimeoutNavigation]);

  // Cleanup polling interval and timeout on unmount
  useEffect(() => {
    return () => {
      // Clean up polling interval when component unmounts
      if (pollingIntervalRef.current) {
        console.log('Cleaning up polling interval on unmount...');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      // Clean up timeout when component unmounts
      if (timeoutRef.current) {
        console.log('Cleaning up timeout on unmount...');
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const handleRetry = () => {
    // Stop polling if active
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Reset polling attempts
    pollAttemptsRef.current = 0;
    setIsCheckingStatus(false);
    
    // Clear stored payment data
    sessionStorage.removeItem('pendingOrderId');
    sessionStorage.removeItem('phonePeMerchantTransactionId');
    sessionStorage.removeItem('paymentInitiated');
    sessionStorage.removeItem('paymentTimestamp');
    sessionStorage.removeItem('pendingPaymentAmount');
 
    sessionStorage.removeItem('mandateUri');
    sessionStorage.removeItem('mandateUriOpened');
    sessionStorage.removeItem('shouldNavigateToProcessing');
    sessionStorage.removeItem('isUpiCollectFlow');
    
    // Navigate back to payment page
    navigate('/trial/subscription-payment');
  };

  return (
    <div className={styles.container}>
      {/* Background Gradient SVG */}
      <div className={styles.backgroundGradient}>
        {/* Bottom Left Corner SVG */}
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="360" 
          height="621" 
          viewBox="0 0 360 621" 
          fill="none"
          className={styles.bottomLeftSvg}
        >
          <g filter="url(#filter0_fn_531_2611)">
            <circle cx="107" cy="592" r="158" fill="#8B5A0A"/>
          </g>
          <defs>
            <filter id="filter0_fn_531_2611" x="-485" y="0" width="1184" height="1184" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
              <feGaussianBlur stdDeviation="217" result="effect1_foregroundBlur_531_2611"/>
              <feTurbulence type="fractalNoise" baseFrequency="0.5 0.5" stitchTiles="stitch" numOctaves="3" result="noise" seed="1097"/>
              <feComponentTransfer in="noise" result="coloredNoise1">
                <feFuncR type="linear" slope="2" intercept="-0.5"/>
                <feFuncG type="linear" slope="2" intercept="-0.5"/>
                <feFuncB type="linear" slope="2" intercept="-0.5"/>
                <feFuncA type="discrete" tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "/>
              </feComponentTransfer>
              <feComposite operator="in" in2="effect1_foregroundBlur_531_2611" in="coloredNoise1" result="noise1Clipped"/>
              <feComponentTransfer in="noise1Clipped" result="color1">
                <feFuncA type="table" tableValues="0 0.3"/>
              </feComponentTransfer>
              <feMerge result="effect2_noise_531_2611">
                <feMergeNode in="effect1_foregroundBlur_531_2611"/>
                <feMergeNode in="color1"/>
              </feMerge>
            </filter>
          </defs>
        </svg>
        {/* Top Right Corner SVG */}
        <svg xmlns="http://www.w3.org/2000/svg" width="360" height="684" viewBox="0 0 360 684" fill="none" className={styles.backgroundSvg}>
          <g filter="url(#filter0_fn_558_8452)">
            <circle cx="360" cy="92" r="158" fill="#C17C0D"/>
          </g>
          <defs>
            <filter id="filter0_fn_558_8452" x="-232" y="-500" width="1184" height="1184" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
              <feGaussianBlur stdDeviation="217" result="effect1_foregroundBlur_558_8452"/>
              <feTurbulence type="fractalNoise" baseFrequency="0.5 0.5" stitchTiles="stitch" numOctaves="3" result="noise" seed="2135"/>
              <feComponentTransfer in="noise" result="coloredNoise1">
                <feFuncR type="linear" slope="2" intercept="-0.5"/>
                <feFuncG type="linear" slope="2" intercept="-0.5"/>
                <feFuncB type="linear" slope="2" intercept="-0.5"/>
                <feFuncA type="discrete" tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "/>
              </feComponentTransfer>
              <feComposite operator="in" in2="effect1_foregroundBlur_558_8452" in="coloredNoise1" result="noise1Clipped"/>
              <feComponentTransfer in="noise1Clipped" result="color1">
                <feFuncA type="table" tableValues="0 0.4"/>
              </feComponentTransfer>
              <feMerge result="effect2_noise_558_8452">
                <feMergeNode in="effect1_foregroundBlur_558_8452"/>
                <feMergeNode in="color1"/>
              </feMerge>
            </filter>
          </defs>
        </svg>
      </div>
      
      {/* Main Content */}
      <div className={styles.content}>
        <div className={styles.iconContainer}>
          <img src={paymentProcessingIcon} alt="Payment Processing" className={styles.icon} />
        </div>
        <h2 className={styles.title}>
          Payment Processing
        </h2>
        
       </div>

      {/* <button className={styles.retryButton} onClick={handleRetry}>
        Cancel Payment
      </button> */}
    </div>
  );
};

export default PaymentProcessing;